"use client";

import {
  Box,
  Checkbox,
  Collapse,
  Menu,
  MenuItem,
  Typography,
} from "@mui/material";
import { useId, useState } from "react";
import { exportToEndNote, exportToCsv } from "@/src/utils/export";
import CopyCitationButton from "@/src/components/common/copy-citation-button/copy-citation-button";
import DisclosureButton from "@/src/components/common/broadsheet/disclosure-button";
import MetaLine from "@/src/components/common/broadsheet/meta-line";
import Panel from "@/src/components/common/broadsheet/panel";
import { simpleDateFormatter } from "@/src/utils/dateFormatter";
import { isValidDate } from "@/src/utils/is-valid-date";
import {
  FONT_DISPLAY,
  FONT_TEXT,
  FONT_UI,
  hairline,
} from "@/src/components/common/broadsheet/tokens";

const toHtmlString = (value: unknown): string => {
  if (Array.isArray(value)) return value.map(toHtmlString).join("");
  if (value === null || value === undefined) return "";
  return String(value);
};

const highlightSx = {
  "& mark": {
    background: "none",
    color: "inherit",
    fontWeight: 700,
    borderBottom: "2px solid",
    borderColor: "custom.kicker",
  },
};

const linkButtonSx = {
  border: 0,
  p: 0,
  background: "none",
  cursor: "pointer",
  fontFamily: FONT_UI,
  fontSize: "0.6875rem",
  fontWeight: 700,
  letterSpacing: "0.08em",
  textTransform: "uppercase" as const,
  color: "primary.main",
  textDecoration: "underline",
  textUnderlineOffset: "3px",
};

const ArticleCard = ({
  article,
  isSelected,
  onSelectToggle,
}: ArticleCardProps) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [abstractOpen, setAbstractOpen] = useState(false);
  const [citeOpen, setCiteOpen] = useState(false);
  const open = Boolean(anchorEl);
  const panelIdPrefix = useId();
  const abstractPanelId = `${panelIdPrefix}-abstract`;
  const citePanelId = `${panelIdPrefix}-how-to-cite`;

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const authors = article.authors.map((author) => {
    const highlighted = article.highlight?.authors?.find(
      (h: string) => h.replace(/<[^>]+>/g, "") === author,
    );
    return highlighted || author;
  });

  const keywords = article.keywords.map((keyword) => {
    const highlighted = article.highlight?.keywords?.find(
      (h: string) => h.replace(/<[^>]+>/g, "") === keyword,
    );
    return highlighted || keyword;
  });

  const title = article.highlight?.title
    ? article.highlight?.title[0]
    : article.title;

  const doi = article.highlight?.doi ? article.highlight?.doi[0] : article.doi;

  function mergeAbstractHighlights(
    abstract: any[],
    highlight?: { "abstract.content"?: string[] },
  ): any {
    if (!highlight || !highlight["abstract.content"]) return abstract;

    const highlighted = highlight["abstract.content"];

    return abstract.map((section) => ({
      ...section,
      content: section.content.map((c: string) => {
        const match = highlighted.find(
          (h: string) => h.replace(/<[^>]+>/g, "").trim() === c.trim(),
        );

        return match || c;
      }),
    }));
  }

  const abstractItems = mergeAbstractHighlights(
    article.abstract,
    article.highlight,
  );

  const abstractSections: Array<{ title: string; content: string }> = (
    Array.isArray(abstractItems) ? abstractItems : []
  )
    .map((section: Record<string, any>) => ({
      title: String(section.title ?? "")
        .replace(/\s*:\s*$/, "")
        .trim(),
      content: toHtmlString(section.content).trim(),
    }))
    .filter((section: { content: string }) => section.content.length > 0);

  const hasAbstract = abstractSections.length > 0;

  const doiUrl = doi
    ? `${process.env.NEXT_PUBLIC_DOI_WEBSITE_URL}${article.doi}`
    : null;

  return (
    <Box
      component="article"
      sx={{
        display: "grid",
        gridTemplateColumns: "auto 1fr",
        columnGap: 0.5,
        alignItems: "start",
        py: { xs: 1, md: 1.25 },
        ...hairline,
        ...(isSelected
          ? {
              borderLeft: "3px solid",
              borderLeftColor: "primary.main",
              pl: 0.75,
              backgroundColor: "custom.newsprintRaised",
            }
          : { borderLeft: "3px solid transparent", pl: 0.75 }),
        "& > *": { minWidth: 0 },
      }}
    >
      {onSelectToggle ? (
        <Checkbox
          size="small"
          checked={Boolean(isSelected)}
          onChange={() => onSelectToggle(article.doi)}
          slotProps={{
            input: {
              "aria-label": `Select article titled ${article.title}`,
            },
          }}
          sx={{ mt: -0.125, p: 0.25 }}
        />
      ) : (
        <Box />
      )}

      <Box>
        <Box
          sx={{
            display: "flex",
            alignItems: "baseline",
            justifyContent: "space-between",
            columnGap: 1,
            rowGap: 0.25,
            flexWrap: "wrap",
          }}
        >
          <Typography
            component="a"
            href={article.url}
            target="_blank"
            rel="noopener noreferrer"
            sx={{
              flex: "1 1 20rem",
              fontFamily: FONT_DISPLAY,
              fontWeight: 700,
              fontSize: { xs: "1.0625rem", md: "1.1875rem" },
              lineHeight: 1.2,
              letterSpacing: "0.01em",
              color: "custom.link",
              textDecoration: "none",
              "&:hover": {
                color: "custom.linkHover",
                textDecoration: "underline",
                textUnderlineOffset: "0.15em",
              },
              ...highlightSx,
            }}
            dangerouslySetInnerHTML={{ __html: toHtmlString(title) }}
          />
          <Box
            component="button"
            type="button"
            onClick={handleMenuOpen}
            aria-haspopup="menu"
            aria-expanded={open}
            sx={{ ...linkButtonSx, flex: "none" }}
          >
            Export
          </Box>
          <Menu anchorEl={anchorEl} open={open} onClose={handleMenuClose}>
            <MenuItem
              onClick={() => {
                exportToEndNote(article);
                handleMenuClose();
              }}
            >
              Export to EndNote
            </MenuItem>
            <MenuItem
              onClick={() => {
                exportToCsv([article]);
                handleMenuClose();
              }}
            >
              Export to CSV
            </MenuItem>
          </Menu>
        </Box>

        <Box
          component="p"
          sx={{
            m: 0,
            mt: 0.25,
            fontFamily: FONT_TEXT,
            fontSize: "0.875rem",
            lineHeight: 1.5,
            color: "custom.inkMuted",
            ...highlightSx,
          }}
          dangerouslySetInnerHTML={{
            __html: toHtmlString(authors.join(", ")),
          }}
        />

        <MetaLine
          sx={{ mt: 0.25 }}
          items={[
            article.journal?.title,
            article.volume && article.issue
              ? `Vol. ${article.volume}, Issue ${article.issue}`
              : null,
            isValidDate(new Date(article.epub))
              ? simpleDateFormatter(article.epub, {
                  year: "numeric",
                  month: "long",
                  day: "numeric",
                })
              : null,
            doiUrl ? (
              <Box
                key="doi"
                component="a"
                href={doiUrl}
                target="_blank"
                rel="noopener noreferrer"
                sx={{ color: "inherit" }}
              >
                {doiUrl}
              </Box>
            ) : null,
          ]}
        />

        {keywords.length > 0 && (
          <Box
            sx={{
              mt: 0.375,
              display: "flex",
              flexWrap: "wrap",
              gap: 0.25,
              fontFamily: FONT_UI,
              fontSize: "0.6875rem",
              color: "custom.inkMuted",
              ...highlightSx,
            }}
          >
            {keywords.map((keyword: any, index: number) => (
              <Box
                key={index}
                component="span"
                sx={{
                  px: 0.375,
                  py: 0.0625,
                  border: "1px solid",
                  borderColor: "custom.ruleHairline",
                }}
                dangerouslySetInnerHTML={{ __html: toHtmlString(keyword) }}
              />
            ))}
          </Box>
        )}

        {(hasAbstract || article.how_to_cite) && (
          <Box
            sx={{
              mt: 0.5,
              display: "flex",
              flexWrap: "wrap",
              justifyContent: "center",
              gap: 0.375,
            }}
          >
            {hasAbstract && (
              <DisclosureButton
                label="Abstract"
                open={abstractOpen}
                onToggle={() => setAbstractOpen(!abstractOpen)}
                controls={abstractPanelId}
              />
            )}
            {article.how_to_cite && (
              <DisclosureButton
                label="How to Cite"
                open={citeOpen}
                onToggle={() => setCiteOpen(!citeOpen)}
                controls={citePanelId}
              />
            )}
          </Box>
        )}

        {hasAbstract && (
          <Collapse in={abstractOpen}>
            <Panel
              id={abstractPanelId}
              sx={{
                mt: 0.5,
                fontFamily: FONT_TEXT,
                fontSize: "0.875rem",
                lineHeight: 1.6,
                color: "custom.ink",
                "& div + div": { mt: 0.5 },
                ...highlightSx,
              }}
              dangerouslySetInnerHTML={{
                __html: abstractSections
                  .map((section) =>
                    section.title
                      ? `<div><strong>${section.title}: </strong>${section.content}</div>`
                      : `<div>${section.content}</div>`,
                  )
                  .join(""),
              }}
            />
          </Collapse>
        )}

        {article.how_to_cite && (
          <Collapse in={citeOpen}>
            <Panel
              id={citePanelId}
              sx={{
                mt: 0.5,
                display: "flex",
                alignItems: "flex-start",
                gap: 0.5,
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  flexGrow: 1,
                  color: "custom.inkMuted",
                  fontSize: "0.875rem",
                  lineHeight: 1.6,
                }}
              >
                {article.how_to_cite}
              </Typography>
              <CopyCitationButton text={article.how_to_cite} />
            </Panel>
          </Collapse>
        )}
      </Box>
    </Box>
  );
};

export default ArticleCard;
